import React, { useMemo, useState } from "react";
import {
  Search,
  Copy,
  CheckCircle,
  ChevronRight,
  Key,
  Shield,
  Server,
} from "lucide-react";

const badgeTone = (method) => {
  switch (method) {
    case "GET":
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    case "POST":
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-200";
    case "DELETE":
      return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
    case "PATCH":
      return "bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    default:
      return "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
  }
};

const Card = ({ className = "", children }) => (
  <div
    className={`rounded-2xl border border-slate-200/70 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const SoftButton = ({ active, className = "", children, ...props }) => (
  <button
    className={`w-full text-left px-2.5 py-2 text-sm rounded-xl transition-colors duration-150 flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50 ${
      active
        ? "bg-blue-50 text-slate-900 font-medium ring-1 ring-blue-200"
        : "text-slate-700 hover:bg-slate-50"
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);

const CodeBox = ({ language, code, onCopy, copied }) => (
  <div className="relative">
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-600">
          {language}
        </span>
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50"
          aria-label="Copy"
        >
          {copied ? (
            <>
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="bg-slate-900 text-slate-100 p-4 font-mono text-sm overflow-x-auto">
        {code}
      </pre>
    </div>
  </div>
);

const AirvoyAPIDocs = () => {
  const [activeEndpoint, setActiveEndpoint] = useState("create-bulk-order");
  const [selectedLanguage, setSelectedLanguage] = useState("javascript");
  const [copiedCode, setCopiedCode] = useState(false);

  // —— Data (unchanged text) ——
const endpoints = useMemo(
  () => [
    {
      category: "Authentication",
      items: [{ id: "api-keys", name: "API Keys Overview", method: null, icon: Key }],
    },
    {
      category: "Bulk Orders",
      items: [
        { id: "create-bulk-order", name: "Create bulk order", method: "POST", path: "/api/business/bulk-orders/" },
        { id: "list-bulk-orders", name: "List bulk orders", method: "GET", path: "/api/business/bulk-orders/list/" }, // ✅ FIXED: added /list/
        { id: "get-order-details", name: "Get order details", method: "GET", path: "/api/business/bulk-orders/{orderId}/" },
        { id: "provision-order", name: "Provision order", method: "POST", path: "/api/business/bulk-orders/{orderId}/provision" }, // ✅ FIXED: removed trailing slash
        { id: "create-payment", name: "Create payment intent", method: "POST", path: "/api/business/bulk-orders/create-payment" }, // ✅ ADDED
        { id: "complete-order", name: "Complete bulk order", method: "POST", path: "/api/business/bulk-orders/complete" }, // ✅ ADDED
      ],
    },
    {
      category: "eSIM Management",
      items: [
        { id: "list-esims", name: "List eSIMs", method: "GET", path: "/api/business/esims/list/" }, // ✅ CORRECT
        { id: "get-esim-status", name: "Get eSIM status", method: "GET", path: "/api/business/esims/{esimId}/status/" },
        { id: "download-qr", name: "Download QR code", method: "GET", path: "/api/business/esims/{esimId}/download-qr/" },
        { id: "revoke-esim", name: "Revoke eSIM bundle", method: "DELETE", path: "/api/business/esims/{esimId}/revoke/" },
        { id: "assign-esim", name: "Assign eSIM", method: "POST", path: "/api/business/esims/{esimId}/assign/" }, // ✅ ADDED
        { id: "unassign-esim", name: "Unassign eSIM", method: "POST", path: "/api/business/esims/{esimId}/unassign/" }, // ✅ ADDED
      ],
    },
    {
      category: "Team Management",
      items: [
        // ⚠️ NOTE: This endpoint doesn't exist in your Django URLs - you need to add it!
        // Add to urls.py: path('business/team/list/', views.list_team_members, name='list_team_members'),
        { id: "list-team", name: "List team members", method: "GET", path: "/api/business/team/list/" }, // ✅ FIXED: added /list/
        { id: "add-member", name: "Add team member", method: "POST", path: "/api/business/team/add/" },
        { id: "remove-member", name: "Remove team member", method: "DELETE", path: "/api/business/team/{memberId}/remove/" },
      ],
    },
    {
      category: "API Key Management",
      items: [
        { id: "generate-api-key", name: "Generate API key", method: "POST", path: "/api/business/api-keys/generate/" }, // ✅ ADDED
        { id: "list-api-keys", name: "List API keys", method: "GET", path: "/api/business/api-keys/list/" }, // ✅ ADDED
        { id: "revoke-api-key", name: "Revoke API key", method: "DELETE", path: "/api/business/api-keys/{keyId}/revoke/" }, // ✅ ADDED
        { id: "regenerate-api-key", name: "Regenerate API key", method: "POST", path: "/api/business/api-keys/{keyId}/regenerate/" }, // ✅ ADDED
      ],
    },
    {
      category: "Analytics",
      items: [
        { id: "get-stats", name: "Get business stats", method: "GET", path: "/api/business/stats/" },
        { id: "usage-report", name: "Get usage report", method: "GET", path: "/api/business/usage-report/" },
        { id: "export-esims", name: "Export eSIMs CSV", method: "GET", path: "/api/business/esims/export/" },
      ],
    },
  ],
  []
);

  const endpointDetails = useMemo(
    () => ({
      "api-keys": {
        title: "API Keys Overview",
        description: "Generate and manage API keys for programmatic access to your business account",
        auth: false,
        content: (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">Getting Started</p>
                    <p className="text-sm text-blue-800">
                      Generate your API key from the Business Dashboard → API Keys section. 
                      Your key will only be shown once - save it securely.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <div id="auth">
              <h3 className="text-sm font-semibold mb-3">Authentication</h3>
              <p className="text-sm text-slate-600 mb-3">
                Include your API key in all requests using the <code className="px-2 py-1 bg-slate-100 rounded text-sm">X-API-Key</code> header:
              </p>
              <Card>
                <div className="p-4 font-mono text-sm">
                  <span className="text-slate-500">X-API-Key:</span> <span className="text-blue-600">airvoy_biz_your_api_key_here</span>
                </div>
              </Card>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Permission Levels</h3>
              <div className="space-y-2">
                <Card>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium">Owner</span>
                    </div>
                    <p className="text-sm text-slate-600 ml-6">Full access to all endpoints including team management and API key generation</p>
                  </div>
                </Card>
                <Card>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium">Editor</span>
                    </div>
                    <p className="text-sm text-slate-600 ml-6">Can create orders, provision eSIMs, and manage assignments</p>
                  </div>
                </Card>
                <Card>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-slate-600" />
                      <span className="text-sm font-medium">Reader</span>
                    </div>
                    <p className="text-sm text-slate-600 ml-6">Read-only access to view orders and eSIMs</p>
                  </div>
                </Card>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3">Rate Limits</h3>
              <p className="text-sm text-slate-600">API requests are limited to <strong>100 requests per minute</strong> per API key.</p>
            </div>
          </div>
        ),
      },
      "create-bulk-order": {
        title: "Create bulk order",
        description: "Create a new bulk eSIM order. Minimum 10 eSIMs required.",
        method: "POST",
        path: "/api/business/bulk-orders/",
        auth: true,
        requiredRole: "editor",
        request: {
          body: {
            planName: { type: "string", required: true, description: "Name of the eSIM plan" },
            country: { type: "string", required: true, description: "Country name" },
            countryCode: { type: "string", required: true, description: "ISO country code" },
            duration: { type: "integer", required: true, description: "Plan duration in days" },
            planPrice: { type: "number", required: true, description: "Price per eSIM in USD" },
            planType: { type: "string", required: true, description: '"unlimited" or "fixed"' },
            dataAmount: { type: "string", required: true, description: 'Data amount (e.g., "5GB") or "unlimited"' },
            quantity: { type: "integer", required: true, description: "Number of eSIMs (minimum 10)" },
            assignedEmails: { type: "array", required: false, description: "Optional list of email addresses" },
          },
        },
        response: {
  "200": {
    order: {
      id: "<string>",
      businessId: "<string>",
      businessEmail: "<string>",
      planName: "<string>",
      country: "<string>",
      countryCode: "<string>",
      duration: "<integer>",
      planPrice: "<number>",
      planType: "<string>",
      dataAmount: "<string>",
      quantity: "<integer>",
      totalAmount: "<number>",
      assignedEmails: ["<string>"],
      status: "<string>",
      paymentStatus: "<string>",
      paymentIntentId: "<string>",
      provisionedCount: "<integer>",
      createdAt: "<ISO 8601 datetime>",
      updatedAt: "<ISO 8601 datetime>"
    }
  }
},
        codeExamples: {
          curl: `curl -X POST https://api-airvoy.com/api/business/bulk-orders/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "planName": "Unlimited - 7 Days",\n    "country": "United States",\n    "countryCode": "US",\n    "duration": 7,\n    "planPrice": 49.99,\n    "planType": "unlimited",\n    "dataAmount": "unlimited",\n    "quantity": 25,\n    "assignedEmails": ["user1@company.com", "user2@company.com"]\n  }'`,
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/bulk-orders/', {\n  method: 'POST',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n  },\n  body: JSON.stringify({\n    planName: 'Unlimited - 7 Days',\n    country: 'United States',\n    countryCode: 'US',\n    duration: 7,\n    planPrice: 49.99,\n    planType: 'unlimited',\n    dataAmount: 'unlimited',\n    quantity: 25,\n    assignedEmails: ['user1@company.com', 'user2@company.com']\n  })\n});\n\nconst data = await response.json();\nconsole.log(data);`,
          python: `import requests\n\nurl = 'https://api-airvoy.com/api/business/bulk-orders/'\nheaders = {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n}\npayload = {\n    'planName': 'Unlimited - 7 Days',\n    'country': 'United States',\n    'countryCode': 'US',\n    'duration': 7,\n    'planPrice': 49.99,\n    'planType': 'unlimited',\n    'dataAmount': 'unlimited',\n    'quantity': 25,\n    'assignedEmails': ['user1@company.com', 'user2@company.com']\n}\n\nresponse = requests.post(url, json=payload, headers=headers)\nprint(response.json())`,
        },
      },
      "list-bulk-orders": {
        title: "List bulk orders",
        description: "Retrieve all bulk orders for your business",
        method: "GET",
        path: "/api/business/bulk-orders/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": {
            orders: [
              {
                id: "<string>",
                planName: "<string>",
                country: "<string>",
                quantity: "<integer>",
                totalAmount: "<number>",
                status: "<string>",
                createdAt: "<ISO 8601 datetime>",
              },
            ],
          },
        },
        codeExamples: {
          curl: `curl -X GET https://api-airvoy.com/api/business/bulk-orders/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here"`,
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/bulk-orders/', {\n  method: 'GET',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here'\n  }\n});\n\nconst data = await response.json();\nconsole.log(data.orders);`,
          python: `import requests\n\nurl = 'https://api-airvoy.com/api/business/bulk-orders/'\nheaders = {'X-API-Key': 'airvoy_biz_your_api_key_here'}\n\nresponse = requests.get(url, headers=headers)\nprint(response.json()['orders'])`,
        },
      },
      "download-qr": {
        title: "Download QR code",
        description: "Download QR code PNG image for a specific business eSIM",
        method: "GET",
        path: "/api/business/esims/{esimId}/download-qr/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": "Binary PNG image file",
          "404": { error: "eSIM not found or QR code not available" },
          "403": { error: "Unauthorized - no access to this business" }
        },
        codeExamples: {
          curl: `curl -X GET https://api-airvoy.com/api/business/esims/esim_abc123/download-qr/ \\
        -H "X-API-Key: airvoy_biz_your_api_key_here" \\
        --output qr_code.png`,
          javascript: `const esimId = 'esim_abc123';

      const response = await fetch(\`https://api-airvoy.com/api/business/esims/\${esimId}/download-qr/\`, {
        method: 'GET',
        headers: {
          'X-API-Key': 'airvoy_biz_your_api_key_here'
        }
      });

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      // Download or display the QR code
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qr_code.png';
      a.click();`,
          python: `import requests

      esim_id = 'esim_abc123'
      url = f'https://api-airvoy.com/api/business/esims/{esim_id}/download-qr/'
      headers = {'X-API-Key': 'airvoy_biz_your_api_key_here'}

      response = requests.get(url, headers=headers)

      # Save QR code
      with open('qr_code.png', 'wb') as f:
          f.write(response.content)`,
        }
      },
      "revoke-esim": {
        title: "Revoke eSIM bundle",
        description: "Revoke the latest bundle assignment from a business eSIM. Warning: If this is the last bundle and the eSIM is not installed, it may be returned to the provider after 4 days.",
        method: "DELETE",
        path: "/api/business/esims/{esimId}/revoke/",
        auth: true,
        requiredRole: "editor", // Only owner and editors can revoke
        response: {
          "200": {
            success: true,
            message: "Bundle revoked successfully",
            revoke_data: {
              iccid: "<string>",
              bundle_name: "<string>",
              revoked_at: "<ISO 8601 datetime>",
              return_status: "<string>",
              warning: "If this was the last bundle and eSIM is not installed, it may be returned to eSIM Go after 4 days."
            }
          },
          "400": {
            error: "Cannot revoke bundle",
            details: "Bundle may have been used or is outside refund period"
          },
          "404": {
            error: "Bundle not found or already revoked"
          },
          "403": {
            error: "Unauthorized - only editors can revoke eSIMs"
          }
        },
        codeExamples: {
          curl: `curl -X DELETE https://api-airvoy.com/api/business/esims/esim_abc123/revoke/ \\
        -H "X-API-Key: airvoy_biz_your_api_key_here"`,
          javascript: `const esimId = 'esim_abc123';

      const response = await fetch(\`https://api-airvoy.com/api/business/esims/\${esimId}/revoke/\`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': 'airvoy_biz_your_api_key_here'
        }
      });

      const data = await response.json();
      console.log(data.message);`,
          python: `import requests

      esim_id = 'esim_abc123'
      url = f'https://api-airvoy.com/api/business/esims/{esim_id}/revoke/'
      headers = {'X-API-Key': 'airvoy_biz_your_api_key_here'}

      response = requests.delete(url, headers=headers)
      print(response.json()['message'])`,
        }
      },
      "list-team": {
  title: "List team members",
  description: "Retrieve all team members for your business account",
  method: "GET",
  path: "/api/business/team/",
  auth: true,
  requiredRole: "reader",
  response: {
    "200": {
      members: [
        {
          id: "<string>",
          email: "<string>",
          status: "<string>", // "active" or "invited"
          esimCount: "<integer>",
          addedAt: "<ISO 8601 datetime>"
        }
      ]
    }
  },
  codeExamples: { /* similar to other GET endpoints */ }
      },
      "remove-member": {
        title: "Remove team member",
        description: "Remove a team member from your business account. This will revoke their access and unassign all their eSIMs.",
        method: "DELETE",
        path: "/api/business/team/{memberId}/remove/",
        auth: true,
        requiredRole: "owner", // Only owners can remove team members
        response: {
          "200": {
            success: true,
            message: "Team member removed successfully",
            email: "<string>",
            unassignedEsims: "<integer>"
          },
          "403": {
            error: "Unauthorized - only owners can remove team members"
          },
          "404": {
            error: "Team member not found"
          }
        },
        codeExamples: { /* similar pattern */ }
      },
      "usage-report": {
        title: "Get usage report",
        description: "Generate a detailed usage report for a specific date range",
        method: "GET",
        path: "/api/business/usage-report/",
        auth: true,
        requiredRole: "reader",
        request: {
          queryParams: {
            start_date: { type: "string", required: true, description: "Start date in YYYY-MM-DD format" },
            end_date: { type: "string", required: true, description: "End date in YYYY-MM-DD format" }
          }
        },
        response: {
          "200": {
            report: {
              date_range: {
                start: "<ISO 8601 datetime>",
                end: "<ISO 8601 datetime>"
              },
              summary: {
                total_orders: "<integer>",
                total_esims_purchased: "<integer>",
                total_amount_spent: "<number>"
              },
              orders: [
                {
                  orderId: "<string>",
                  planName: "<string>",
                  country: "<string>",
                  quantity: "<integer>",
                  totalAmount: "<number>",
                  status: "<string>",
                  createdAt: "<ISO 8601 datetime>"
                }
              ],
              esims_by_country: {
                "United States": "<integer>",
                "Canada": "<integer>"
              }
            }
          },
          "400": {
            error: "start_date and end_date required (YYYY-MM-DD)"
          }
        },
        codeExamples: {
          javascript: `const startDate = '2025-01-01';
      const endDate = '2025-01-31';

      const response = await fetch(\`https://api-airvoy.com/api/business/usage-report/?start_date=\${startDate}&end_date=\${endDate}\`, {
        method: 'GET',
        headers: {
          'X-API-Key': 'airvoy_biz_your_api_key_here'
        }
      });

      const data = await response.json();
      console.log(\`Total spent in period: $\${data.report.summary.total_amount_spent}\`);`
        }
      },
      "export-esims": {
        title: "Export eSIMs CSV",
        description: "Export all business eSIMs as a CSV file for reporting and analysis",
        method: "GET",
        path: "/api/business/esims/export/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": "CSV file download with columns: ICCID, Plan Name, Country, Assigned To, Status, Data Remaining (MB), Purchased At"
        },
        codeExamples: {
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/esims/export/', {
        method: 'GET',
        headers: {
          'X-API-Key': 'airvoy_biz_your_api_key_here'
        }
      });

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'business_esims.csv';
      a.click();`,
          python: `import requests
      from datetime import datetime

      url = 'https://api-airvoy.com/api/business/esims/export/'
      headers = {'X-API-Key': 'airvoy_biz_your_api_key_here'}

      response = requests.get(url, headers=headers)

      # Save CSV
      filename = f'business_esims_{datetime.now().strftime("%Y%m%d")}.csv'
      with open(filename, 'wb') as f:
          f.write(response.content)`
        }
      },
      "provision-order": {
        title: "Provision order",
        description: "Provision eSIMs from a paid bulk order with email assignments",
        method: "POST",
        path: "/api/business/bulk-orders/{orderId}/provision",
        auth: true,
        requiredRole: "editor",
        request: {
          body: {
            assignments: { type: "array", required: true, description: "Array of {email, quantity} objects" },
            provision_quantity: { type: "integer", required: true, description: "Total eSIMs to provision" },
            organization_name: { type: "string", required: true, description: "Organization name for emails" },
            custom_message: { type: "string", required: false, description: "Optional message for recipients" },
          },
        },
        response: {
          "200": {
            success: true,
            provisioned: "<integer>",
            failed: "<integer>",
            total_provisioned: "<integer>",
            remaining: "<integer>",
            esims: [
              { id: "<string>", iccid: "<string>", assignedEmail: "<string>", status: "ready" },
            ],
          },
        },
        codeExamples: {
                    curl: `curl -X POST https://api-airvoy.com/api/business/bulk-orders/abc123def456/provision \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "assignments": [\n      {"email": "john@company.com", "quantity": 5},\n      {"email": "sara@company.com", "quantity": 5}\n    ],\n    "provision_quantity": 10,\n    "organization_name": "Acme Corp",\n    "custom_message": "Welcome to the conference!"\n  }'`,
          javascript: `const orderId = 'abc123def456';\n\nconst response = await fetch(\`https://api-airvoy.com/api/business/bulk-orders/${'${orderId}'}/provision\`, {\n  method: 'POST',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n  },\n  body: JSON.stringify({\n    assignments: [\n      { email: 'john@company.com', quantity: 5 },\n      { email: 'sara@company.com', quantity: 5 }\n    ],\n    provision_quantity: 10,\n    organization_name: 'Acme Corp',\n    custom_message: 'Welcome to the conference!'\n  })\n});\n\nconst data = await response.json();\nconsole.log(\`Provisioned ${'${data.provisioned}'} eSIMs\`);`,
          python: `import requests\n\norder_id = 'abc123def456'\nurl = f'https://api-airvoy.com/api/business/bulk-orders/{order_id}/provision'\nheaders = {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n}\npayload = {\n    'assignments': [\n        {'email': 'john@company.com', 'quantity': 5},\n        {'email': 'sara@company.com', 'quantity': 5}\n    ],\n    'provision_quantity': 10,\n    'organization_name': 'Acme Corp',\n    'custom_message': 'Welcome to the conference!'\n}\n\nresponse = requests.post(url, json=payload, headers=headers)\nprint(f"Provisioned {response.json()['provisioned']} eSIMs")`,
        },
      },
      "list-esims": {
        title: "List eSIMs",
        description: "Retrieve all provisioned eSIMs for your business",
        method: "GET",
        path: "/api/business/esims/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": {
            esims: [
              {
                id: "<string>",
                iccid: "<string>",
                planName: "<string>",
                country: "<string>",
                assignedEmail: "<string>",
                status: "<string>",
                dataRemaining: "<number>",
                purchasedAt: "<ISO 8601 datetime>",
              },
            ],
          },
        },
        codeExamples: {
          curl: `curl -X GET https://api-airvoy.com/api/business/esims/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here"`,
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/esims/', {\n  method: 'GET',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here'\n  }\n});\n\nconst data = await response.json();\nconsole.log(\`Total eSIMs: ${'${data.esims.length}'}\`);`,
          python: `import requests\n\nurl = 'https://api-airvoy.com/api/business/esims/'\nheaders = {'X-API-Key': 'airvoy_biz_your_api_key_here'}\n\nresponse = requests.get(url, headers=headers)\nesims = response.json()['esims']\nprint(f'Total eSIMs: {len(esims)}')`,
        },
      },
      "get-esim-status": {
        title: "Get eSIM status",
        description: "Get real-time usage data for a specific eSIM from the provider",
        method: "GET",
        path: "/api/business/esims/{esimId}/status/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": {
            success: true,
            esim_data: { id: "<string>", iccid: "<string>", bundle_name: "<string>", country_name: "<string>" },
            bundle_status: {
              bundle_state: "<string>",
              initial_quantity: "<integer>",
              remaining_quantity: "<integer>",
              usage_percent: "<number>",
              initial_quantity_formatted: "<string>",
              remaining_quantity_formatted: "<string>",
              last_updated: "<ISO 8601 datetime>",
            },
          },
        },
        codeExamples: {
                    curl: `curl -X GET https://api-airvoy.com/api/business/esims/esim_abc123/status/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here"`,
          javascript: `const esimId = 'esim_abc123';\n\nconst response = await fetch(\`https://api-airvoy.com/api/business/esims/${'${esimId}'}/status/\`, {\n  method: 'GET',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here'\n  }\n});\n\nconst data = await response.json();\nconsole.log(\`Data usage: ${'${data.bundle_status.usage_percent}'}%\`);`,
          python: `import requests\n\nesim_id = 'esim_abc123'\nurl = f'https://api-airvoy.com/api/business/esims/{esim_id}/status/'\nheaders = {'X-API-Key': 'airvoy_biz_your_api_key_here'}\n\nresponse = requests.get(url, headers=headers)\nstatus = response.json()['bundle_status']\nprint(f"Data usage: {status['usage_percent']}%")`,

        },
      },
      "add-member": {
        title: "Add team member",
        description: "Add a new team member with specified role (reader or editor)",
        method: "POST",
        path: "/api/business/team/add/",
        auth: true,
        requiredRole: "owner",
        request: {
          body: {
            email: { type: "string", required: true, description: "Team member email address" },
            role: { type: "string", required: true, description: '"reader" or "editor"' },
          },
        },
        response: {
          "200": {
            success: true,
            memberId: "<string>",
            email: "<string>",
            role: "<string>",
            status: "<string>",
            message: "<string>",
          },
        },
        codeExamples: {
          curl: `curl -X POST https://api-airvoy.com/api/business/team/add/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "email": "newmember@company.com",\n    "role": "editor"\n  }'`,
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/team/add/', {\n  method: 'POST',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n  },\n  body: JSON.stringify({\n    email: 'newmember@company.com',\n    role: 'editor'\n  })\n});\n\nconst data = await response.json();\nconsole.log(data.message);`,
          python: `import requests\n\nurl = 'https://api-airvoy.com/api/business/team/add/'\nheaders = {\n    'X-API-Key': 'airvoy_biz_your_api_key_here',\n    'Content-Type': 'application/json'\n}\npayload = {\n    'email': 'newmember@company.com',\n    'role': 'editor'\n}\n\nresponse = requests.post(url, json=payload, headers=headers)\nprint(response.json()['message'])`,

        },
      },
      "get-stats": {
        title: "Get business stats",
        description: "Retrieve comprehensive statistics for your business account",
        method: "GET",
        path: "/api/business/stats/",
        auth: true,
        requiredRole: "reader",
        response: {
          "200": {
            stats: {
              orders: { total: "<integer>", pending: "<integer>", approved: "<integer>", total_spent: "<number>" },
              esims: { total: "<integer>", active: "<integer>", assigned: "<integer>", unassigned: "<integer>" },
              team: { members: "<integer>" },
            },
          },
        },
        codeExamples: {
          curl: `curl -X GET https://api-airvoy.com/api/business/stats/ \\\n  -H "X-API-Key: airvoy_biz_your_api_key_here"`,
          javascript: `const response = await fetch('https://api-airvoy.com/api/business/stats/', {\n  method: 'GET',\n  headers: {\n    'X-API-Key': 'airvoy_biz_your_api_key_here'\n  }\n});\n\nconst data = await response.json();\nconsole.log(\`Total spent: $${'${data.stats.orders.total_spent}'}\`);`,
          python: `import requests\n\nurl = 'https://api-airvoy.com/api/business/stats/'\nheaders = {'X-API-Key': 'airvoy_biz_your_api_key_here'}\n\nresponse = requests.get(url, headers=headers)\nstats = response.json()['stats']\nprint(f"Total spent: ${'{'}stats['orders']['total_spent']{'}'}")`,

        },
      },
    }),
    []
  );

  const activeDetails = endpointDetails[activeEndpoint] || endpointDetails["api-keys"];

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    window.setTimeout(() => setCopiedCode(false), 1600);
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_left,rgba(29,78,216,0.06),transparent_40%),radial-gradient(ellipse_at_bottom_right,rgba(2,132,199,0.06),transparent_40%)]">

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[16rem_1fr] xl:grid-cols-[16rem_1fr_15rem] gap-6 px-4 py-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-16 h-max">
          <Card>
            <div className="p-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white shadow-inner placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                />
              </div>
            </div>
            <div className="px-3 pb-3">
              {endpoints.map((section, idx) => (
                <div key={idx} className="mb-4">
                  <div className="text-[11px] font-semibold text-slate-500 uppercase mb-2 px-1 tracking-wider">
                    {section.category}
                  </div>
                  <div className="space-y-1">
                    {section.items.map((item) => (
                      <SoftButton
                        key={item.id}
                        active={activeEndpoint === item.id}
                        onClick={() => setActiveEndpoint(item.id)}
                      >
                        {item.method ? (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${badgeTone(item.method)}`}>
                            {item.method}
                          </span>
                        ) : (
                          item.icon && <item.icon className="w-4 h-4" />
                        )}
                        <span className="truncate">{item.name}</span>
                      </SoftButton>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
            <span>API Reference</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900">{activeDetails.title}</span>
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight mb-1">{activeDetails.title}</h1>
              <p className="text-slate-600">{activeDetails.description}</p>
            </div>

            {activeDetails.method && (
              <Card className="border-blue-100">
                <div className="flex flex-wrap items-center gap-3 p-4 bg-gradient-to-br from-slate-50 to-white rounded-2xl">
                  <span className={`text-sm px-3 py-1.5 rounded-full font-semibold ${badgeTone(activeDetails.method)}`}>
                    {activeDetails.method}
                  </span>
                  <code className="text-sm font-mono text-slate-800">{activeDetails.path}</code>
                </div>
              </Card>
            )}

            {activeDetails.auth && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <Key className="w-4 h-4 text-amber-700" />
                  <span className="text-amber-900 font-medium">Requires API Key</span>
                </div>
                {activeDetails.requiredRole && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                    <Shield className="w-4 h-4 text-purple-700" />
                    <span className="text-purple-900 font-medium">
                      Role: {activeDetails.requiredRole.charAt(0).toUpperCase() + activeDetails.requiredRole.slice(1)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {activeDetails.content && <div className="space-y-4">{activeDetails.content}</div>}

            {activeDetails.request?.body && (
              <section id="request" className="space-y-3">
                <h2 className="text-xl font-semibold">Request Body</h2>
                <div className="text-sm text-slate-600">application/json</div>
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-3 font-medium text-slate-700">Parameter</th>
                          <th className="text-left p-3 font-medium text-slate-700">Type</th>
                          <th className="text-left p-3 font-medium text-slate-700">Required</th>
                          <th className="text-left p-3 font-medium text-slate-700">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {Object.entries(activeDetails.request.body).map(([key, value]) => (
                          <tr key={key}>
                            <td className="p-3 font-mono text-blue-600">{key}</td>
                            <td className="p-3 text-slate-600">{value.type}</td>
                            <td className="p-3">
                              {value.required ? (
                                <span className="text-[11px] bg-rose-50 text-rose-700 px-2 py-1 rounded-md font-medium ring-1 ring-rose-200">
                                  required
                                </span>
                              ) : (
                                <span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-1 rounded-md ring-1 ring-slate-200">
                                  optional
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-slate-600">{value.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </section>
            )}

            {activeDetails.response && (
              <section id="response" className="space-y-3">
                <h2 className="text-xl font-semibold">Response</h2>
                <div className="space-y-4">
                  {Object.entries(activeDetails.response).map(([statusCode, responseBody]) => (
                    <Card key={statusCode}>
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-[11px] px-2 py-1 rounded-md font-semibold ring-1 ${
                              statusCode.startsWith("2")
                                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                : "bg-rose-50 text-rose-700 ring-rose-200"
                            }`}
                          >
                            {statusCode}
                          </span>
                          <span className="text-sm text-slate-600">
                            {statusCode.startsWith("2") ? "Success" : "Error"}
                          </span>
                        </div>
                        <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-sm overflow-x-auto">
                          <pre>{JSON.stringify(responseBody, null, 2)}</pre>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {activeDetails.codeExamples && (
              <section id="examples" className="space-y-3">
                <h2 className="text-xl font-semibold">Code Examples</h2>

                <div className="flex items-center gap-1.5 mb-2 border-b border-slate-200">
                  {Object.keys(activeDetails.codeExamples).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setSelectedLanguage(lang)}
                      className={`px-3 py-2 text-sm font-medium transition border-b-2 -mb-px ${
                        selectedLanguage === lang
                          ? "text-blue-600 border-blue-600"
                          : "text-slate-600 hover:text-slate-900 border-transparent"
                      }`}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>

                <CodeBox
                  language={selectedLanguage}
                  code={activeDetails.codeExamples[selectedLanguage]}
                  onCopy={() => copyCode(activeDetails.codeExamples[selectedLanguage])}
                  copied={copiedCode}
                />
              </section>
            )}

            {activeDetails.method && (
              <div className="py-6"></div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AirvoyAPIDocs;
